import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Database } from "lucide-react";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";
import type { SilaeFormData, SilaeSourceFormData } from "@/types/silae";
import { createEmptySource } from "@/hooks/useSilaeWizardFormik";
import { SourceItemConfig } from "./SourceItemConfig";

interface BaseSourceConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string; // Ex: "ifBranch.bonuses.0" ou "elseBranch.bonuses.0"
  title?: string;
  description?: string;
  className?: string;
}

/**
 * Composant principal pour gérer la liste des sources de base
 * Cette configuration permet de gérer une liste de sources de base pour un bonus.
 */
export const BaseSourceConfig: React.FC<BaseSourceConfigProps> = ({
  formik,
  fieldPrefix,
  title,
  description,
  className,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("source-0");

  const finalTitle = title || t("baseSourceConfig.title");
  const finalDescription = description || t("baseSourceConfig.description");

  const basesPath = `${fieldPrefix}.bases`;
  const bases: SilaeSourceFormData[] = useMemo(
    () => getIn(formik.values, basesPath) || [],
    [formik.values, basesPath],
  );

  // Fonction pour vérifier si une source a des erreurs
  const hasSourceErrors = useCallback(
    (index: number): boolean => {
      const sourceErrors = getIn(formik.errors, `${basesPath}.${index}`);
      if (!sourceErrors) return false;
      if (typeof sourceErrors === "string") return true;
      if (typeof sourceErrors === "object") {
        return Object.keys(sourceErrors).length > 0;
      }
      return false;
    },
    [formik.errors, basesPath],
  );

  const addSource = useCallback(() => {
    const newSource = createEmptySource();
    const updatedBases = [...bases, newSource];

    // Si on a plusieurs sources (updatedBases.length > 1), les conditions sont obligatoires
    // Si on a une seule source, les conditions sont optionnelles (garder la valeur actuelle)
    const hasMultipleSources = updatedBases.length > 1;

    // Mettre à jour hasConditions pour toutes les sources
    const updatedBasesWithConditions = updatedBases.map((base) => ({
      ...base,
      hasConditions: hasMultipleSources ? true : (base.hasConditions ?? false),
    }));

    formik.setFieldValue(basesPath, updatedBasesWithConditions);
    setActiveTab(`source-${updatedBases.length - 1}`);
  }, [bases, formik, basesPath]);

  const removeSource = useCallback(
    (index: number) => {
      if (bases.length <= 1) return;
      const updatedBases = bases.filter((_, i) => i !== index);

      // Si on a plusieurs sources (updatedBases.length > 1), les conditions sont obligatoires
      // Si on a une seule source, les conditions sont optionnelles (garder la valeur actuelle)
      const hasMultipleSources = updatedBases.length > 1;

      // Mettre à jour hasConditions pour toutes les sources
      const updatedBasesWithConditions = updatedBases.map((base) => ({
        ...base,
        hasConditions: hasMultipleSources
          ? true
          : (base.hasConditions ?? false),
      }));

      formik.setFieldValue(basesPath, updatedBasesWithConditions);
      if (activeTab === `source-${index}`) {
        setActiveTab("source-0");
      }
    },
    [bases, formik, basesPath, activeTab],
  );

  return (
    <Card className={`bg-background dark:bg-background mb-0 ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`text-lg`}>{finalTitle}</CardTitle>
            <CardDescription>{finalDescription}</CardDescription>
          </div>
          {bases.length < 5 && (
            <Button
              type="button"
              onClick={addSource}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {t("baseSourceConfig.addSource")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {bases.length === 1 ? (
          // Une seule source : affichage direct sans tabs
          <SourceItemConfig
            formik={formik}
            sourceFieldPrefix={`${basesPath}.0`}
            totalSources={bases.length}
            sourceIndex={0}
          />
        ) : (
          // Plusieurs sources : utiliser les tabs
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full gap-1 justify-start overflow-x-auto flex-wrap h-auto">
              {bases.map((source, index) => (
                <div key={source.id} className="flex items-center gap-1">
                  <TabsTrigger
                    value={`source-${index}`}
                    className={`flex items-center gap-2 ${
                      hasSourceErrors(index)
                        ? "border-1 border-red-500 animate-pulse"
                        : ""
                    }`}
                  >
                    <Database className="h-3 w-3" />
                    {t("baseSourceConfig.source")} {index + 1}
                    {bases.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSource(index);
                        }}
                        className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-2 w-2" />
                      </Button>
                    )}
                  </TabsTrigger>
                </div>
              ))}
            </TabsList>

            {bases.map((_, index) => (
              <TabsContent key={index} value={`source-${index}`}>
                <SourceItemConfig
                  formik={formik}
                  sourceFieldPrefix={`${basesPath}.${index}`}
                  totalSources={bases.length}
                  sourceIndex={index}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
